import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { FileText, Lock, Unlock, UserCircle2, Check, ChevronsUpDown, Plus, CheckCircle, History, Printer, X, MessageSquare, Clock, Receipt } from "lucide-react";
import { HistoryPanel } from "@/components/HistoryPanel";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { confirmDirtyClose, useFormDirty } from "@/lib/dialogConfirm";
import { OperatorMultiSelect } from "@/components/ui/operator-multi-select";

// Ente coinvolto options based on practice type
// 30/06/2026 — Le costanti hardcoded ENTE_OPTIONS_AGENZIA / ENTE_OPTIONS_STUDIO
// e TIPO_PRATICA_OPTIONS sono state migrate nel DB (`config_table_entries`)
// e ora sono caricate dinamicamente via `configTables` props (vedi App.js).
// Vedi `/app/memory/CHANGELOG.md` voce 30/06/2026 — Config Tables.

/**
 * Calculate 10 business days from a given date (Mon-Fri only)
 */
function addBusinessDays(startDate, days) {
  const date = new Date(startDate);
  let added = 0;
  while (added < days) {
    date.setDate(date.getDate() + 1);
    const dayOfWeek = date.getDay();
    // Skip Saturday (6) and Sunday (0)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      added++;
    }
  }
  return date.toISOString().split('T')[0];
}

/**
 * Format number as Italian currency (1.500,00)
 */
function formatItalianCurrency(value) {
  if (!value && value !== 0) return "";
  const num = parseFloat(String(value).replace(/[^\d.,]/g, '').replace(',', '.'));
  if (isNaN(num)) return value;
  return num.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Parse Italian formatted number back to float
 */
function parseItalianNumber(value) {
  if (!value) return "";
  const cleaned = String(value).replace(/\./g, '').replace(',', '.');
  const num = parseFloat(cleaned);
  return isNaN(num) ? "" : num;
}

// Multi-select for Ente Coinvolto with horizontal layout
function EnteMultiSelect({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const scrollRef = React.useRef(null);
  
  // Parse value to array
  const selectedEnti = useMemo(() => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    return value.split(', ').filter(Boolean);
  }, [value]);

  const toggleEnte = (ente) => {
    const newSelected = selectedEnti.includes(ente)
      ? selectedEnti.filter(e => e !== ente)
      : [...selectedEnti, ente];
    onChange(newSelected.join(', '));
    setOpen(false);
  };

  const removeEnte = (ente) => {
    const newSelected = selectedEnti.filter(e => e !== ente);
    onChange(newSelected.join(', '));
  };

  // Native wheel scroll handler - directly manipulates scrollTop
  const handleWheel = useCallback((e) => {
    const container = scrollRef.current;
    if (!container) return;
    
    // Prevent Radix from intercepting
    e.stopPropagation();
    
    // Manually scroll the container
    container.scrollTop += e.deltaY;
  }, []);

  return (
    <div className="md:col-span-2">
      <Label htmlFor="enteCoinvolto">Ente Coinvolto * (selezione multipla)</Label>
      {/* Selected items horizontal display */}
      {selectedEnti.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {selectedEnti.map((ente) => (
            <Badge 
              key={ente} 
              variant="secondary" 
              className="text-xs flex items-center gap-1 px-2 py-1"
            >
              {ente}
              <button
                type="button"
                onClick={() => removeEnte(ente)}
                className="ml-1 hover:text-red-500"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen} modal={false}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            data-testid="case-ente-select"
            className="w-full justify-between font-normal"
          >
            {selectedEnti.length > 0 
              ? `${selectedEnti.length} ente/i selezionato/i`
              : "Seleziona o cerca ente coinvolto"}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-[--radix-popover-trigger-width] p-0" 
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command>
            <CommandInput placeholder="Cerca ente..." />
            <CommandList 
              ref={scrollRef}
              style={{ 
                maxHeight: '300px', 
                overflowY: 'auto',
                overscrollBehavior: 'contain',
                WebkitOverflowScrolling: 'touch'
              }}
              onWheel={handleWheel}
            >
              <CommandEmpty>Nessun ente trovato.</CommandEmpty>
              <CommandGroup>
                {options.map((ente) => (
                  <CommandItem
                    key={ente}
                    value={ente}
                    onSelect={() => toggleEnte(ente)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedEnti.includes(ente) ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {ente}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// Searchable Combobox for Client with alphabetical ordering and quick create
// Fixed: Uses native DOM elements with ref-based scroll handling to bypass Radix scroll locking
function ClientSearchCombobox({ value, onChange, clients, onCreateClient }) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const scrollRef = React.useRef(null);

  // Sort clients alphabetically with null safety
  const sortedClients = useMemo(() => {
    if (!clients || !Array.isArray(clients)) return [];
    return [...clients].sort((a, b) => 
      (a.fullName || "").localeCompare(b.fullName || "", 'it')
    );
  }, [clients]);

  // Filter clients based on search
  const filteredClients = useMemo(() => {
    if (!searchTerm) return sortedClients;
    const term = searchTerm.toLowerCase();
    return sortedClients.filter(client => 
      (client.fullName || "").toLowerCase().includes(term) ||
      (client.fiscalCode || "").toLowerCase().includes(term) ||
      (client.email || "").toLowerCase().includes(term)
    );
  }, [sortedClients, searchTerm]);

  // Memoized selected client lookup with null safety
  const selectedClient = useMemo(() => {
    if (!value || !clients || !Array.isArray(clients)) return null;
    return clients.find(c => String(c.id) === String(value)) || null;
  }, [clients, value]);

  // Stable click handler
  const handleSelectClient = useCallback((clientId) => {
    onChange(String(clientId));
    setOpen(false);
    setSearchTerm("");
  }, [onChange]);

  // Stable create handler
  const handleCreateClick = useCallback(() => {
    setOpen(false);
    setSearchTerm("");
    if (onCreateClient) onCreateClient();
  }, [onCreateClient]);

  // Native wheel scroll handler - directly manipulates scrollTop
  const handleWheel = useCallback((e) => {
    const container = scrollRef.current;
    if (!container) return;
    
    // Prevent Radix from intercepting
    e.stopPropagation();
    
    // Manually scroll the container
    container.scrollTop += e.deltaY;
  }, []);

  return (
    <div className="md:col-span-2">
      <Label htmlFor="clientId">Cliente *</Label>
      <div className="flex gap-2">
        <Popover open={open} onOpenChange={setOpen} modal={false}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              data-testid="case-client-select"
              className="flex-1 justify-between font-normal"
            >
              {selectedClient ? selectedClient.fullName : "Cerca o seleziona cliente..."}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent 
            className="w-[--radix-popover-trigger-width] p-0" 
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            {/* Search input - native element */}
            <div className="p-2 border-b">
              <Input
                placeholder="Cerca cliente per nome, CF, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-9"
                autoFocus
              />
            </div>
            {/* Scrollable list with ref-based wheel handling */}
            <div 
              ref={scrollRef}
              className="p-1"
              style={{ 
                maxHeight: '300px', 
                overflowY: 'auto', 
                overscrollBehavior: 'contain',
                WebkitOverflowScrolling: 'touch'
              }}
              onWheel={handleWheel}
            >
                {filteredClients.length === 0 && searchTerm && (
                  <div className="p-3 text-center">
                    <p className="text-sm text-slate-500 mb-2">Nessun cliente trovato per "{searchTerm}"</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={handleCreateClick}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Crea nuovo cliente
                    </Button>
                  </div>
                )}
                {filteredClients.length === 0 && !searchTerm && clients.length === 0 && (
                  <p className="p-3 text-sm text-slate-500 text-center">Nessun cliente presente</p>
                )}
                {filteredClients.map((client) => (
                  <button
                    key={client.id}
                    type="button"
                    className={cn(
                      "w-full flex items-center gap-2 p-2 text-left rounded-md transition-colors",
                      "hover:bg-slate-100 focus:bg-slate-100 focus:outline-none",
                      String(value) === String(client.id) && "bg-blue-50"
                    )}
                    onClick={() => handleSelectClient(client.id)}
                  >
                    <Check
                      className={cn(
                        "h-4 w-4 shrink-0",
                        String(value) === String(client.id) ? "opacity-100 text-blue-600" : "opacity-0"
                      )}
                    />
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="truncate font-medium">{client.fullName}</span>
                      {client.fiscalCode && (
                        <span className="text-xs text-slate-400 truncate">{client.fiscalCode}</span>
                      )}
                    </div>
                  </button>
                ))}
            </div>
          </PopoverContent>
        </Popover>
        <Button
          type="button"
          variant="outline"
          data-testid="create-client-from-case-button"
          onClick={handleCreateClick}
          className="shrink-0"
        >
          <Plus className="h-4 w-4 mr-1" />
          Crea Cliente
        </Button>
      </div>
    </div>
  );
}

// Searchable Combobox for Tipo Pratica (Studio only)
function TipoPraticaCombobox({ value, onChange, options, visible }) {
  const [open, setOpen] = useState(false);

  if (!visible) return null;

  return (
    <div>
      <Label htmlFor="practiceArea">Tipo Pratica *</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            data-testid="case-area-select"
            className="w-full justify-between font-normal"
          >
            {value || "Seleziona o cerca tipo pratica"}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder="Cerca tipo pratica..." />
            <CommandList
              style={{ maxHeight: '300px', overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}
              onWheel={(e) => {
                // Same Radix-inside-Dialog wheel-eating fix applied to the
                // shadcn Select: when this Popover opens within the
                // CaseDialog the outer overlay can swallow wheel events.
                // We scroll the CommandList programmatically and stop
                // propagation, while still letting the parent take over
                // at the top/bottom edges so the surrounding form remains
                // scrollable.
                const el = e.currentTarget;
                const { scrollTop, scrollHeight, clientHeight } = el;
                const atTop = scrollTop <= 0 && e.deltaY < 0;
                const atBottom =
                  scrollTop + clientHeight >= scrollHeight && e.deltaY > 0;
                if (atTop || atBottom) return;
                e.stopPropagation();
                e.preventDefault();
                el.scrollTop += e.deltaY;
              }}
            >
              <CommandEmpty>Nessun tipo trovato.</CommandEmpty>
              <CommandGroup>
                {options.map((tipo) => (
                  <CommandItem
                    key={tipo}
                    value={tipo}
                    onSelect={(currentValue) => {
                      onChange(currentValue === value ? "" : currentValue);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === tipo ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {tipo}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function CaseDialog({
  isOpen,
  onOpenChange,
  editingCase,
  caseForm,
  setCaseForm,
  onSave,
  onReset,
  onInitNew,
  onAfterSave,
  onSaveAndInvoice,
  clients,
  operators,
  cases,
  getOperatorName,
  generatePreviewCaseNumberSync,
  fetchNextCaseNumber,
  isSaving,
  onOpenClientDialog,
  currentOperator,
  onPrintMandato,
  caseHistory = [],
  // 30/06/2026 — Tabelle configurabili (Tipo Pratica + Ente Coinvolto).
  // Forma: { practice_types: [{id, name, isActive}], ente_options: [{id, name, scope, isActive}] }
  // Caricate da App.js al boot (1 fetch unico, no API durante typing).
  configTables = { practice_types: [], ente_options: [] },
}) {
  // Track if opening date has been confirmed
  const [openingDateConfirmed, setOpeningDateConfirmed] = useState(false);
  // Track if due date was manually set
  const [dueDateManuallySet, setDueDateManuallySet] = useState(false);
  // Track formatted fee display
  const [feeDisplay, setFeeDisplay] = useState("");
  // Track if case number is unlocked for manual editing
  const [numberUnlocked, setNumberUnlocked] = useState(false);
  // Track new note input
  const [newNote, setNewNote] = useState("");
  // Ref to track pending invoice flow
  const pendingInvoiceRef = React.useRef(false);
  
  // Effect: when status changes to "Nuova richiesta con fattura" and pending, trigger save
  useEffect(() => {
    if (pendingInvoiceRef.current && caseForm.status === "Nuova richiesta con fattura" && !editingCase) {
      pendingInvoiceRef.current = false;
      (async () => {
        const created = await onSave();
        if (created) {
          onReset();
          if (onSaveAndInvoice) onSaveAndInvoice(created);
        }
      })();
    }
  }, [caseForm.status]);

  // ─── Wave 30/06/2026 — Opzioni dinamiche da config_tables ──────────
  // `enteOptions` ora si basa su `configTables.ente_options` filtrate per:
  //  - scope corrispondente al `practiceType` corrente (studio | agenzia | both)
  //  - isActive == true
  // PIÙ la voce storica (CSV multi-select) corrente, anche se disattivata,
  // così pratiche storiche con voci ora-disattivate restano leggibili.
  // Ordinato alfabetico (case-insensitive).
  const enteOptions = useMemo(() => {
    const isStudio = caseForm.practiceType === "Gestionale Studio";
    const wantedScope = isStudio ? "studio" : "agenzia";
    const active = (configTables.ente_options || []).filter(o => {
      if (!o.isActive) return false;
      return o.scope === wantedScope || o.scope === "both";
    });
    // Inclusione voci legacy disattivate ancora selezionate (CSV string).
    const currentRaw = caseForm.enteCoinvolto || "";
    const currentNames = currentRaw.split(",").map(s => s.trim()).filter(Boolean);
    const activeNames = new Set(active.map(o => o.name));
    const legacy = (configTables.ente_options || [])
      .filter(o => currentNames.includes(o.name) && !activeNames.has(o.name))
      .map(o => o.name);
    const all = [...active.map(o => o.name), ...legacy];
    return Array.from(new Set(all)).sort((a, b) => a.localeCompare(b, "it"));
  }, [configTables.ente_options, caseForm.practiceType, caseForm.enteCoinvolto]);

  // `tipoPraticaOptions` = practice_types attivi + eventuale voce legacy
  // (la `practiceArea` corrente se ora disattivata). Ordinato alfabetico.
  const tipoPraticaOptions = useMemo(() => {
    const all = configTables.practice_types || [];
    const active = all.filter(o => o.isActive).map(o => o.name);
    const cur = (caseForm.practiceArea || "").trim();
    if (cur && !active.includes(cur)) {
      const legacy = all.find(o => o.name === cur);
      if (legacy) active.push(cur);
    }
    return Array.from(new Set(active)).sort((a, b) => a.localeCompare(b, "it"));
  }, [configTables.practice_types, caseForm.practiceArea]);

  // Tipo Pratica is visible for all practice types
  const showTipoPratica = true;

  // Initialize fee display when form loads
  useEffect(() => {
    if (caseForm.estimatedFee) {
      setFeeDisplay(formatItalianCurrency(caseForm.estimatedFee));
    } else {
      setFeeDisplay("");
    }
  }, [editingCase]);

  // Auto-calculate due date when opening date changes (only if not manually set)
  useEffect(() => {
    if (!editingCase && caseForm.openingDate && !dueDateManuallySet) {
      const openingDate = new Date(caseForm.openingDate);
      if (!isNaN(openingDate.getTime())) {
        const defaultDue = new Date(openingDate);
        defaultDue.setDate(defaultDue.getDate() + 10);
        const defaultDueDate = defaultDue.toISOString().split('T')[0];
        if (caseForm.dueDate !== defaultDueDate) {
          setCaseForm(prev => ({ ...prev, dueDate: defaultDueDate }));
        }
      }
    }
  }, [caseForm.openingDate, editingCase, dueDateManuallySet]);

  // Reset states when dialog opens/closes
  useEffect(() => {
    if (isOpen && !editingCase) {
      setOpeningDateConfirmed(false);
      setDueDateManuallySet(false);
      setFeeDisplay("");
      setNumberUnlocked(false);
      // Calculate default due date when dialog opens
      if (caseForm.openingDate && !caseForm.dueDate) {
        const openingDate = new Date(caseForm.openingDate);
        if (!isNaN(openingDate.getTime())) {
          const defaultDue = new Date(openingDate);
          defaultDue.setDate(defaultDue.getDate() + 10);
          const defaultDueDate = defaultDue.toISOString().split('T')[0];
          setCaseForm(prev => ({ ...prev, dueDate: defaultDueDate }));
        }
      }
    } else if (isOpen && editingCase) {
      setOpeningDateConfirmed(true); // Already saved cases are considered confirmed
      setDueDateManuallySet(true);
      setNumberUnlocked(false);
      if (editingCase.estimatedFee) {
        setFeeDisplay(formatItalianCurrency(editingCase.estimatedFee));
      }
    }
  }, [isOpen, editingCase, caseForm.openingDate, setCaseForm]);

  // Handler for practice type change — fetches real next number from backend
  const handlePracticeTypeChange = async (value) => {
    // Determina i nuovi enti disponibili filtrando configTables per scope.
    const newScope = value === "Gestionale Studio" ? "studio" : "agenzia";
    const newEnteSet = new Set(
      (configTables.ente_options || [])
        .filter(o => o.isActive && (o.scope === newScope || o.scope === "both"))
        .map(o => o.name)
    );
    // Filtra le voci multi-select già selezionate, mantenendo solo quelle
    // ancora valide nello scope nuovo (UX coerente).
    const currentNames = (caseForm.enteCoinvolto || "")
      .split(",").map(s => s.trim()).filter(Boolean);
    const keptNames = currentNames.filter(n => newEnteSet.has(n));
    const newEnte = keptNames.join(", ");
    const newTipoPratica = value === "Gestionale Studio" ? caseForm.practiceArea : "";
    
    // Immediately show sync preview, then update with real number
    const syncPreview = generatePreviewCaseNumberSync(value);
    setCaseForm(prev => ({ 
      ...prev, 
      practiceType: value,
      caseNumber: syncPreview,
      enteCoinvolto: newEnte,
      practiceArea: newTipoPratica
    }));
    
    if (!editingCase && fetchNextCaseNumber) {
      const realNumber = await fetchNextCaseNumber(value, caseForm.openingDate);
      setCaseForm(prev => prev.practiceType === value ? { ...prev, caseNumber: realNumber } : prev);
    }
  };

  // Update preview number when opening date year changes (for Agenzia)
  const lastPreviewRef = React.useRef({ type: '', year: '' });
  useEffect(() => {
    if (editingCase || !isOpen || !fetchNextCaseNumber) return;
    const year = (caseForm.openingDate || '').slice(0, 4);
    const type = caseForm.practiceType;
    const key = `${type}|${year}`;
    if (lastPreviewRef.current.type === type && lastPreviewRef.current.year === year) return;
    lastPreviewRef.current = { type, year };
    
    fetchNextCaseNumber(type, caseForm.openingDate).then(num => {
      setCaseForm(prev => {
        if (prev.practiceType === type && (prev.openingDate || '').slice(0, 4) === year) {
          return { ...prev, caseNumber: num };
        }
        return prev;
      });
    }).catch(() => {});
  }, [caseForm.openingDate, caseForm.practiceType, editingCase, isOpen, fetchNextCaseNumber, setCaseForm]);

  // Handle opening date confirmation
  const handleConfirmOpeningDate = () => {
    setOpeningDateConfirmed(true);
  };

  // Handle opening date change
  const handleOpeningDateChange = (value) => {
    setOpeningDateConfirmed(false);
    setCaseForm({ ...caseForm, openingDate: value });
  };

  // Handle due date change (mark as manually set)
  const handleDueDateChange = (value) => {
    setDueDateManuallySet(true);
    setCaseForm({ ...caseForm, dueDate: value });
  };

  // Handle fee input with formatting
  const handleFeeChange = (e) => {
    const rawValue = e.target.value;
    setFeeDisplay(rawValue);
  };

  // Handle fee blur - format the value
  const handleFeeBlur = () => {
    const numericValue = parseItalianNumber(feeDisplay);
    if (numericValue !== "") {
      setFeeDisplay(formatItalianCurrency(numericValue));
      setCaseForm({ ...caseForm, estimatedFee: numericValue });
    } else {
      setFeeDisplay("");
      setCaseForm({ ...caseForm, estimatedFee: "" });
    }
  };

  // Handle operator change with history tracking. Supports multi-assignment:
  // when an array is passed the first element becomes the legacy
  // `assignedOperatorId` and the full list goes to `assignedOperatorIds`.
  const handleOperatorChange = (valueOrList) => {
    const list = Array.isArray(valueOrList)
      ? valueOrList.filter(Boolean)
      : (valueOrList && valueOrList !== "none" ? [valueOrList] : []);
    const newOperatorId = list[0] || "";
    const currentTime = new Date().toISOString();

    // Build history entry (tracks the FIRST/primary operator).
    const historyEntry = {
      timestamp: currentTime,
      fromOperatorId: caseForm.assignedOperatorId || null,
      toOperatorId: newOperatorId || null,
      changedBy: currentOperator?.id || null
    };
    
    // Append to existing history
    const existingHistory = caseForm.operatorHistory || [];
    const newHistory = [...existingHistory, historyEntry];

    setCaseForm({
      ...caseForm,
      assignedOperatorId: newOperatorId,
      assignedOperatorIds: list,
      operatorHistory: newHistory
    });
  };

  // Get operator history display
  const operatorHistory = editingCase?.operatorHistory || caseForm.operatorHistory || [];

  // Handle adding a new note with operator tracking
  const handleAddNote = () => {
    if (!newNote.trim()) return;
    
    // Get operator name correctly (fullName field)
    const operatorName = currentOperator?.fullName || currentOperator?.name || "Utente";
    
    const noteEntry = {
      text: newNote.trim(),
      operatorId: currentOperator?.id || null,
      operatorName: operatorName,
      timestamp: new Date().toISOString()
    };
    
    const existingNotes = caseForm.structuredNotes || [];
    const updatedNotes = [...existingNotes, noteEntry];
    
    // Also update legacy internalNotes for backward compatibility
    const legacyFormat = updatedNotes.map(n => 
      `[${new Date(n.timestamp).toLocaleString('it-IT')} - ${n.operatorName}] ${n.text}`
    ).join('\n');
    
    setCaseForm({ 
      ...caseForm, 
      structuredNotes: updatedNotes,
      internalNotes: legacyFormat
    });
    setNewNote("");
  };

  // Parse existing notes to structured format (backward compatibility)
  const displayNotes = useMemo(() => {
    // If we have structured notes, use them
    if (caseForm.structuredNotes && caseForm.structuredNotes.length > 0) {
      return caseForm.structuredNotes;
    }
    // Otherwise, show legacy notes as a single entry
    if (caseForm.internalNotes) {
      return [{
        text: caseForm.internalNotes,
        operatorId: null,
        operatorName: "Sistema",
        timestamp: editingCase?.createdAt || new Date().toISOString()
      }];
    }
    return [];
  }, [caseForm.structuredNotes, caseForm.internalNotes, editingCase]);

  // Format history entry description
  const formatHistoryEntry = (entry) => {
    switch (entry.actionType) {
      case "created":
        return "Pratica creata";
      case "status_change":
        return `Stato: ${entry.previousStatus || "—"} → ${entry.newStatus || "—"}`;
      case "operator_change":
        const fromOp = entry.previousOperatorId ? getOperatorName(entry.previousOperatorId) : "Nessuno";
        const toOp = entry.newOperatorId ? getOperatorName(entry.newOperatorId) : "Nessuno";
        return `Operatore: ${fromOp} → ${toOp}`;
      case "notes_change":
        return entry.description || "Note modificate";
      case "field_change":
        return `${entry.fieldName || "Campo"}: ${entry.previousValue || "—"} → ${entry.newValue || "—"}`;
      default:
        return entry.description || entry.actionType;
    }
  };

  // Dirty-tracking guard: confirm before discarding unsaved data.
  const isDirty = useFormDirty(caseForm, isOpen);
  const guardedOpenChange = (next) => {
    if (!next && !confirmDirtyClose(isDirty)) return;
    onOpenChange(next);
  };

  return (
    <Dialog open={isOpen} onOpenChange={guardedOpenChange}>
      <DialogTrigger asChild>
        <Button
          data-testid="new-case-button"
          size="sm"
          className="gis-btn-primary hover:scale-105 shadow-md transition-all duration-200"
          onClick={() => {
            if (onInitNew) onInitNew();
            onOpenChange(true);
          }}
        >
          <FileText className="mr-2 h-4 w-4" />
          Nuova Pratica
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl p-0 gap-0 overflow-hidden border-t-[6px] border-t-[var(--gis-green)]" data-testid="case-dialog-content">
        {/* Barra brand v1.5.0 — banda verde in cima ai dialoghi principali */}
        
        <DialogHeader>
          <DialogTitle className="gis-heading" style={{ color: "var(--gis-green)" }}>
            {editingCase ? "Modifica Pratica" : "Nuova Pratica"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4" data-testid="case-dialog-body">
        {editingCase?.sourceQuoteId && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 flex items-center gap-2" data-testid="case-source-quote-link">
            <span className="text-emerald-700 font-medium">Preventivo di origine:</span>
            <button
              type="button"
              onClick={() => {
                try {
                  window.dispatchEvent(new CustomEvent("gi:open-quote", {
                    detail: {
                      quoteId: editingCase.sourceQuoteId,
                      quoteNumber: editingCase.sourceQuoteNumber || "",
                    },
                  }));
                  // Chiude il dialog corrente per lasciar navigare la UI
                  if (typeof onOpenChange === "function") onOpenChange(false);
                } catch (_) { /* noop */ }
              }}
              className="inline-flex items-center gap-1 rounded border border-emerald-300 bg-white px-2 py-0.5 font-mono text-xs text-emerald-700 hover:bg-emerald-100 hover:border-emerald-400 transition-colors"
              title="Apri il preventivo di origine"
              data-testid="case-source-quote-button"
            >
              N. {editingCase.sourceQuoteNumber || editingCase.sourceQuoteId}
            </button>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
          {/* Row 1: Practice Type and Number */}
          <div>
            <Label htmlFor="practiceType">Tipologia Gestionale *</Label>
            <Select
              value={caseForm.practiceType}
              onValueChange={handlePracticeTypeChange}
              disabled={editingCase && caseForm.isNumberLocked}
            >
              <SelectTrigger data-testid="case-practice-type-select">
                <SelectValue placeholder="Seleziona tipologia" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Gestionale Studio">Gestionale Studio</SelectItem>
                <SelectItem value="Gestionale Agenzia">Gestionale Agenzia</SelectItem>
              </SelectContent>
            </Select>
            {editingCase && caseForm.isNumberLocked && (
              <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                <Lock className="h-3 w-3" /> Non modificabile dopo salvataggio
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="caseNumber">Numero Pratica {editingCase ? "*" : "(anteprima)"}</Label>
            <div className="flex items-center gap-2">
              <Input
                id="caseNumber"
                data-testid="case-number-input"
                value={caseForm.caseNumber}
                onChange={(e) =>
                  setCaseForm({ ...caseForm, caseNumber: e.target.value })
                }
                placeholder="Es. GS-0001"
                disabled={!numberUnlocked}
                className={cn(!numberUnlocked && "bg-slate-100")}
              />
              {!editingCase && !numberUnlocked && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setNumberUnlocked(true)}
                  className="shrink-0"
                  data-testid="unlock-number-button"
                >
                  <Unlock className="h-4 w-4 mr-1" />
                  Modifica numero
                </Button>
              )}
              {!editingCase && numberUnlocked && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    setNumberUnlocked(false);
                    if (fetchNextCaseNumber) {
                      const realNum = await fetchNextCaseNumber(caseForm.practiceType, caseForm.openingDate);
                      setCaseForm(prev => ({ ...prev, caseNumber: realNum }));
                    } else {
                      const previewNumber = generatePreviewCaseNumberSync(caseForm.practiceType);
                      setCaseForm(prev => ({ ...prev, caseNumber: previewNumber }));
                    }
                  }}
                  className="shrink-0"
                  data-testid="lock-number-button"
                >
                  <Lock className="h-4 w-4 mr-1" />
                  Auto
                </Button>
              )}
              {editingCase && caseForm.isNumberLocked && (
                <Lock className="h-4 w-4 text-slate-400" />
              )}
            </div>
            {!editingCase && !numberUnlocked && (
              <p className="text-xs text-slate-500 mt-1">
                Numero finale generato automaticamente dal server al salvataggio
              </p>
            )}
            {!editingCase && numberUnlocked && (
              <p className="text-xs text-amber-600 mt-1">
                Modalità manuale: inserisci un numero univoco
              </p>
            )}
          </div>

          {/* Row 2: Opening Date/Time with Confirm button */}
          <div>
            <Label htmlFor="openingDate">Data e Ora Apertura Pratica *</Label>
            <div className="flex items-center gap-2">
              <Input
                id="openingDate"
                type="datetime-local"
                data-testid="case-opening-date-input"
                value={caseForm.openingDate ? caseForm.openingDate.slice(0, 16) : ""}
                onChange={(e) => handleOpeningDateChange(e.target.value)}
                disabled={editingCase}
                className={cn(
                  editingCase ? "bg-slate-100" : "",
                  "flex-1"
                )}
              />
              {!editingCase && (
                <Button
                  type="button"
                  variant={openingDateConfirmed ? "secondary" : "outline"}
                  size="sm"
                  onClick={handleConfirmOpeningDate}
                  disabled={!caseForm.openingDate}
                  className={cn(
                    "shrink-0 transition-all",
                    openingDateConfirmed && "bg-green-100 text-green-700 border-green-300"
                  )}
                  data-testid="confirm-opening-date-btn"
                >
                  {openingDateConfirmed ? (
                    <>
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Confermata
                    </>
                  ) : (
                    "Conferma"
                  )}
                </Button>
              )}
            </div>
            {!editingCase && !openingDateConfirmed && caseForm.openingDate && (
              <p className="text-xs text-amber-600 mt-1">
                Clicca "Conferma" per fissare la data di apertura
              </p>
            )}
          </div>
          
          {/* Due Date - with 10 business days default */}
          <div>
            <Label htmlFor="dueDate">Scadenza Pratica *</Label>
            <Input
              id="dueDate"
              type="date"
              data-testid="case-due-date-input"
              value={caseForm.dueDate}
              onChange={(e) => handleDueDateChange(e.target.value)}
              required
            />
            {!editingCase && !dueDateManuallySet && caseForm.dueDate && (
              <p className="text-xs text-slate-500 mt-1">
                Default: 10 giorni dalla data di apertura
              </p>
            )}
          </div>

          {/* Row 2.5: Client with searchable combobox and "Crea Cliente" (full width) */}
          <ClientSearchCombobox
            value={caseForm.clientId}
            onChange={(value) => {
              const client = clients.find(c => String(c.id) === String(value));
              const isLordo = client && (
                client.clientType === "Privato"
                || !client.vatNumber
                || String(client.vatNumber).trim() === ""
              );
              setCaseForm({
                ...caseForm,
                clientId: value,
                perContoDi: null,
                feeMode: isLordo ? "lordo" : "netto",
              });
            }}
            clients={clients}
            onCreateClient={onOpenClientDialog}
          />

          {/* "Per conto di" section — only for Azienda / Ditta individuale.
              30/06/2026 — Aggiunto selettore esplicito "Per conto di chi"
              SOPRA la sezione anagrafica:
                - "Per se stesso" (solo Ditta individuale) → nasconde i
                  sotto-campi e li rende non obbligatori (pcd = null).
                - "Per il Legale Rappresentante" | "Per il Liquidatore" |
                  "Per il Delegato" → mostra la sezione e auto-imposta
                  pcd.qualifica al valore corrispondente.
              Backward compat: pratiche legacy senza `perContoTipo` ma con
              `perContoDi.qualifica` derivano il valore al volo. */}
          {(() => {
            const selectedClient = clients.find(c => c.id === caseForm.clientId);
            const isCompany = selectedClient && ["Azienda", "Ditta individuale"].includes(selectedClient.clientType);
            if (!isCompany) return null;
            const isDittaInd = selectedClient.clientType === "Ditta individuale";
            const pcd = caseForm.perContoDi || { nome: "", cognome: "", codiceFiscale: "", email: "", cellulare: "", qualifica: "Legale Rappresentante" };

            // Deriva `perContoTipo` corrente (legacy fallback).
            let perContoTipo = caseForm.perContoTipo;
            if (!perContoTipo) {
              const q = pcd.qualifica || "Legale Rappresentante";
              perContoTipo = `Per il ${q}`;
            }
            const isPerSeStesso = perContoTipo === "Per se stesso";

            const setPerContoTipo = (val) => {
              if (val === "Per se stesso") {
                // Nasconde sotto-campi + azzera pcd (no obblighi).
                setCaseForm(prev => ({ ...prev, perContoTipo: val, perContoDi: null }));
              } else {
                // Auto-imposta qualifica corrispondente, preservando dati pcd.
                const qualifica = val.replace(/^Per il\s+/, "");
                const prevPcd = caseForm.perContoDi || { nome: "", cognome: "", codiceFiscale: "", email: "", cellulare: "" };
                setCaseForm(prev => ({
                  ...prev,
                  perContoTipo: val,
                  perContoDi: { ...prevPcd, qualifica },
                }));
              }
            };
            const updatePcd = (field, val) => setCaseForm(prev => ({ ...prev, perContoDi: { ...(prev.perContoDi || pcd), [field]: val } }));
            const hasValidation = !pcd.email && !pcd.cellulare;

            return (
              <div className="md:col-span-2 border border-orange-200 rounded-lg p-3 bg-orange-50/50" data-testid="per-conto-di-section">
                <div className="mb-3">
                  <Label className="text-sm font-semibold text-orange-700">Per conto di chi *</Label>
                  <Select value={perContoTipo} onValueChange={setPerContoTipo}>
                    <SelectTrigger className="h-8 text-sm mt-1" data-testid="per-conto-tipo-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {isDittaInd && (
                        <SelectItem value="Per se stesso" data-testid="per-conto-opt-se-stesso">Per se stesso</SelectItem>
                      )}
                      <SelectItem value="Per il Legale Rappresentante">Per il Legale Rappresentante</SelectItem>
                      <SelectItem value="Per il Liquidatore">Per il Liquidatore</SelectItem>
                      <SelectItem value="Per il Delegato">Per il Delegato</SelectItem>
                    </SelectContent>
                  </Select>
                  {isPerSeStesso && (
                    <p className="text-xs text-orange-600 mt-1 italic" data-testid="pcd-se-stesso-notice">
                      Pratica svolta direttamente per il titolare della ditta individuale. Nessun dato aggiuntivo richiesto.
                    </p>
                  )}
                </div>

                {!isPerSeStesso && (
                  <>
                    <p className="text-xs font-semibold text-orange-700 mb-2">Dati persona fisica</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Nome *</Label>
                        <Input value={pcd.nome} onChange={e => updatePcd("nome", e.target.value)} placeholder="Nome" className="h-8 text-sm" data-testid="pcd-nome" />
                      </div>
                      <div>
                        <Label className="text-xs">Cognome *</Label>
                        <Input value={pcd.cognome} onChange={e => updatePcd("cognome", e.target.value)} placeholder="Cognome" className="h-8 text-sm" data-testid="pcd-cognome" />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Codice Fiscale *</Label>
                        <Input value={pcd.codiceFiscale} onChange={e => updatePcd("codiceFiscale", e.target.value.toUpperCase())} placeholder="RSSMRA80A01H501Z" className="h-8 text-sm font-mono" data-testid="pcd-cf" />
                      </div>
                      <div>
                        <Label className="text-xs">Email</Label>
                        <Input type="email" value={pcd.email} onChange={e => updatePcd("email", e.target.value)} placeholder="email@esempio.it" className="h-8 text-sm" data-testid="pcd-email" />
                      </div>
                      <div>
                        <Label className="text-xs">Cellulare</Label>
                        <Input value={pcd.cellulare} onChange={e => updatePcd("cellulare", e.target.value)} placeholder="333 1234567" className="h-8 text-sm" data-testid="pcd-cellulare" />
                      </div>
                    </div>
                    {/* Hidden input per mantenere il valore qualifica leggibile a stampa/PDF.
                        Visualmente lo abbiamo "sintetizzato" nel selettore superiore. */}
                    {pcd.qualifica === "Delegato" && (
                      <p className="text-xs text-blue-600 mt-2 italic" data-testid="pcd-delegato-notice">Nel mandato verrà inclusa la dichiarazione: "Dichiaro di aver ricevuto diretto incarico dal legale rappresentante."</p>
                    )}
                    {hasValidation && (pcd.nome || pcd.cognome || pcd.codiceFiscale) && (
                      <p className="text-xs text-red-500 mt-2" data-testid="pcd-validation-error">Inserire almeno Email o Cellulare</p>
                    )}
                  </>
                )}
              </div>
            );
          })()}

          {/* Row 3: Ente Coinvolto (full width) - Multi-select with horizontal layout */}
          <EnteMultiSelect
            value={caseForm.enteCoinvolto || ""}
            onChange={(value) => setCaseForm({ ...caseForm, enteCoinvolto: value })}
            options={enteOptions}
          />

          {/* Row 3.5: Tipo Pratica immediately below Ente (only for Studio) */}
          <TipoPraticaCombobox
            value={caseForm.practiceArea || ""}
            onChange={(value) => setCaseForm({ ...caseForm, practiceArea: value })}
            options={tipoPraticaOptions}
            visible={showTipoPratica}
          />

          {/* Row 4: Subject (full width) - Updated label */}
          <div className="md:col-span-2">
            <Label htmlFor="subject">Descrizione Pratica (utile anche per fattura) *</Label>
            <Input
              id="subject"
              data-testid="case-subject-input"
              value={caseForm.subject}
              onChange={(e) =>
                setCaseForm({ ...caseForm, subject: e.target.value })
              }
              placeholder="Es. Pratica per ..."
            />
            <p className="text-xs text-slate-500 mt-1">
              Questo campo viene usato come descrizione nella fattura collegata
            </p>
          </div>

          {/* Row 5: Channel and Fee (with Italian formatting) */}
          <div>
            <Label htmlFor="requestChannel">Canale Richiesta</Label>
            <Select
              value={caseForm.requestChannel}
              onValueChange={(value) =>
                setCaseForm({ ...caseForm, requestChannel: value })
              }
            >
              <SelectTrigger data-testid="case-channel-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Telefono">Telefono</SelectItem>
                <SelectItem value="Email">Email</SelectItem>
                <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                <SelectItem value="Di persona">Di persona</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="estimatedFee">Compenso Previsto (EUR)</Label>
            <Input
              id="estimatedFee"
              type="text"
              inputMode="decimal"
              data-testid="case-fee-input"
              value={feeDisplay}
              onChange={handleFeeChange}
              onBlur={handleFeeBlur}
              placeholder="Es. 1.500,00"
            />
          </div>
          <div>
            <Label htmlFor="feeMode">Modalità del compenso</Label>
            <Select
              value={caseForm.feeMode || "lordo"}
              onValueChange={(value) => setCaseForm({ ...caseForm, feeMode: value })}
            >
              <SelectTrigger id="feeMode" data-testid="case-fee-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lordo">Lordo IVA inclusa</SelectItem>
                <SelectItem value="netto">Netto imponibile</SelectItem>
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-slate-500">
              Acconti e saldo saranno detratti sempre nella stessa modalità.
            </p>
          </div>

          {/* Row 5b: Di cui diritti e bolli (unified field, replaces Importo Diritti + Importo Bolli) */}
          <div className="md:col-span-2">
            <Label htmlFor="importoDirittiBolli">Di cui diritti e bolli (EUR)</Label>
            <Input
              id="importoDirittiBolli"
              type="number"
              step="0.01"
              min="0"
              data-testid="case-diritti-bolli-input"
              value={caseForm.importoDirittiBolli || ""}
              onChange={(e) => setCaseForm({ ...caseForm, importoDirittiBolli: e.target.value })}
              placeholder="0,00"
            />
          </div>

          {/* Row 6: Operators - Creator and Assigned */}
          {/* Creator Operator */}
          <div>
            <Label>Operatore Creatore</Label>
            <div className="flex items-center gap-2 h-10 px-3 border rounded-md bg-slate-50">
              <UserCircle2 className="h-4 w-4 text-slate-400" />
              <span className="text-sm text-slate-600">
                {editingCase && editingCase.creatorOperatorId 
                  ? getOperatorName(editingCase.creatorOperatorId)
                  : currentOperator?.fullName || "Operatore corrente"
                }
              </span>
              <Lock className="h-3 w-3 text-slate-400 ml-auto" />
            </div>
            <p className="text-xs text-slate-400 mt-1">
              {editingCase ? "Non modificabile" : "Verrà impostato automaticamente"}
            </p>
          </div>

          {/* Assigned Operator — multi-select (20/02/2026) */}
          <div>
            <Label htmlFor="assignedOperatorId">Operatore Assegnato</Label>
            <OperatorMultiSelect
              operators={operators}
              value={caseForm.assignedOperatorIds && caseForm.assignedOperatorIds.length
                ? caseForm.assignedOperatorIds
                : (caseForm.assignedOperatorId ? [caseForm.assignedOperatorId] : [])}
              onChange={handleOperatorChange}
              placeholder="Seleziona uno o più operatori…"
              dataTestid="case-operator-multi-select"
            />
            {operators.filter(op => op.status !== "sospeso").length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                Nessun operatore attivo registrato. Vai in Operatori per aggiungerne.
              </p>
            )}
          </div>

          {/* Row 7: Priority and Status */}
          <div>
            <Label htmlFor="priority">Priorità</Label>
            <Select
              value={caseForm.priority}
              onValueChange={(value) =>
                setCaseForm({ ...caseForm, priority: value })
              }
            >
              <SelectTrigger data-testid="case-priority-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Alta">Alta</SelectItem>
                <SelectItem value="Media">Media</SelectItem>
                <SelectItem value="Bassa">Bassa</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="status">Stato</Label>
            <Select
              value={caseForm.status}
              onValueChange={(value) =>
                setCaseForm({ ...caseForm, status: value })
              }
            >
              <SelectTrigger data-testid="case-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Nuova richiesta">Nuova richiesta</SelectItem>
                <SelectItem value="Aperta">Aperta</SelectItem>
                <SelectItem value="Da contattare">Da contattare</SelectItem>
                <SelectItem value="In lavorazione">In lavorazione</SelectItem>
                <SelectItem value="In attesa documenti">In attesa documenti</SelectItem>
                <SelectItem value="Sospesa">Sospesa</SelectItem>
                <SelectItem value="Conclusa">Conclusa</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Row 8: Description */}
          <div className="md:col-span-2">
            <Label htmlFor="description">Dettagli pratica</Label>
            <Textarea
              id="description"
              data-testid="case-description-textarea"
              value={caseForm.description}
              onChange={(e) =>
                setCaseForm({ ...caseForm, description: e.target.value })
              }
              rows={3}
              placeholder="Note aggiuntive e dettagli sulla pratica"
            />
          </div>

          {/* Row 9: Documents */}
          <div className="md:col-span-2">
            <Label htmlFor="requestedDocuments">
              Documenti Richiesti (separati da virgola)
            </Label>
            <Input
              id="requestedDocuments"
              data-testid="case-documents-input"
              value={caseForm.requestedDocuments}
              onChange={(e) =>
                setCaseForm({ ...caseForm, requestedDocuments: e.target.value })
              }
              placeholder="Es. Documento identità, Codice fiscale, Curriculum"
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="internalNotes" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Note Interne
            </Label>
            
            {/* Display existing notes - SINGLE LINE FORMAT: [Data] [Ora] [Operatore] [Testo] */}
            {displayNotes.length > 0 && (
              <ScrollArea className="h-[80px] mt-2 mb-2 border rounded-md p-2 bg-slate-50">
                <div className="space-y-1">
                  {displayNotes.map((note, idx) => {
                    const noteDate = new Date(note.timestamp);
                    const dateStr = noteDate.toLocaleDateString('it-IT');
                    const timeStr = noteDate.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
                    return (
                      <div key={idx} className="text-xs flex items-baseline gap-2 py-0.5 border-b border-slate-200 last:border-0">
                        <span className="text-slate-400 whitespace-nowrap">{dateStr}</span>
                        <span className="text-slate-400 whitespace-nowrap">{timeStr}</span>
                        <span className="font-medium text-blue-600 whitespace-nowrap">{note.operatorName}</span>
                        <span className="text-slate-700 truncate flex-1">{note.text}</span>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
            
            {/* Add new note */}
            <div className="flex gap-2">
              <Textarea
                id="newNote"
                data-testid="case-new-note-textarea"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                rows={2}
                placeholder="Scrivi una nuova nota..."
                className="flex-1"
              />
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={handleAddNote}
                disabled={!newNote.trim()}
                className="self-end"
              >
                <Plus className="h-4 w-4 mr-1" />
                Aggiungi
              </Button>
            </div>
            {currentOperator && (
              <p className="text-xs text-slate-400 mt-1">
                La nota sarà firmata come: {currentOperator.fullName || currentOperator.name}
              </p>
            )}
          </div>

          {/* Full Change History Section - SINGLE LINE FORMAT: [Data] [Ora] [Operatore] [Descrizione] */}
          {editingCase && caseHistory.length > 0 && (
            <div className="md:col-span-2">
              <Label className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Storico Modifiche
              </Label>
              <ScrollArea className="h-[80px] mt-2 border rounded-md p-2 bg-amber-50">
                <div className="space-y-1">
                  {caseHistory.map((entry, idx) => {
                    const entryDate = new Date(entry.timestamp);
                    const dateStr = entryDate.toLocaleDateString('it-IT');
                    const timeStr = entryDate.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
                    const operatorName = entry.operatorId ? getOperatorName(entry.operatorId) : "Sistema";
                    return (
                      <div key={entry.id || idx} className="text-xs flex items-baseline gap-2 py-0.5 border-b border-amber-200 last:border-0">
                        <span className="text-slate-400 whitespace-nowrap">{dateStr}</span>
                        <span className="text-slate-400 whitespace-nowrap">{timeStr}</span>
                        <span className="font-medium text-blue-600 whitespace-nowrap">{operatorName}</span>
                        <span className="text-slate-700 truncate flex-1">{formatHistoryEntry(entry)}</span>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>

        {/* 16/07/2026 — Timeline Cliente 360°: HistoryPanel con filtri
            per sorgente (Fatture, Incassi, Note, Telefonate…). Alimentata
            da case.history che il backend popola con action codes
            strutturati (`invoice_created`, `payment_added`, ecc.). */}
        {editingCase && Array.isArray(editingCase.history) && editingCase.history.length > 0 && (
          <div className="border-t border-slate-200 pt-3 mt-2" data-testid="case-timeline">
            <div className="flex items-center gap-2 mb-2">
              <History className="h-4 w-4 text-slate-600" />
              <span className="text-sm font-semibold text-slate-800">Timeline pratica</span>
              <span className="text-[11px] text-slate-500">
                {editingCase.history.length} event{editingCase.history.length === 1 ? "o" : "i"}
              </span>
            </div>
            <HistoryPanel
              events={editingCase.history}
              testId={`case-timeline-panel-${editingCase.id}`}
            />
          </div>
        )}

        </div>
        {/* Bottom buttons */}
        <DialogFooter className="flex flex-row gap-2 justify-start flex-wrap">
          <Button 
            data-testid="save-case-button" 
            onClick={async () => { 
              const result = await onSave(); 
              if (result) { 
                onReset(); 
                if (onAfterSave) onAfterSave(); 
              } 
            }}
            disabled={!caseForm.dueDate || isSaving}
            className="gis-btn-primary"
          >
            {isSaving ? "Salvataggio..." : "Salva e Chiudi"}
          </Button>
          <Button 
            type="button"
            variant="secondary"
            data-testid="print-mandato-button"
            onClick={() => onPrintMandato && onPrintMandato(editingCase || caseForm)}
            className="flex items-center gap-2"
          >
            <Printer className="h-4 w-4" />
            Stampa Pratica
          </Button>
          {!editingCase && onSaveAndInvoice && (
            <Button 
              type="button"
              variant="outline"
              data-testid="save-and-invoice-button"
              onClick={() => {
                pendingInvoiceRef.current = true;
                setCaseForm(prev => ({ ...prev, status: "Nuova richiesta con fattura" }));
              }}
              disabled={!caseForm.dueDate || isSaving}
              className="flex items-center gap-2 gis-border-green gis-text-green hover:bg-[var(--gis-green-50)]"
            >
              <Receipt className="h-4 w-4" />
              Crea pratica e vai in fatturazione
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
